import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private mediaObjectUrlCache = new Map<string, string>();
  private mediaObjectUrlInflight = new Map<string, Observable<string>>();
 
  constructor(
    public toastr: ToastrService,
    public router: Router,
    private injector: Injector,
  ) {
   
   }

  private get http(): HttpClient {
    return this.injector.get(HttpClient);
  }
  showSuccess(message: string, title?: string) {   
    this.toastr.success(message, title);
  }
  showError(message: string, title?: string) {
    this.toastr.error(message, title, {timeOut: 3000});
  }
  showInfo(message: string, title?: string) {
    this.toastr.info(message, title, {timeOut: 3000});
  }
  showWarning(message: string, title?: string) {
    this.toastr.warning(message, title, {timeOut: 3000});
  }
  isEmpty(value: unknown): boolean {
    if (value == null) return true;
    if (Array.isArray(value) || typeof value === 'string') return value.length === 0;
    if (value instanceof Map || value instanceof Set) return value.size === 0;
    if (typeof value === 'object') return Object.keys(value as object).length === 0;
    return false;
  }
  isEmptyObject(obj: Record<string, unknown>): boolean {
    return Object.keys(obj).length === 0;
  }

  searchCarrierRepository(data: {
    page: number;
    size: number;
    sortBy: string;
    sortOrder: string;
    search: string;
    filter: unknown[];
    filterType?: string;
  }): Observable<{ data?: any[]; total?: number }> {
    let urlAppend = '';
    if (!this.isEmpty(data.filterType)) {
      urlAppend = '&filterType=' + encodeURIComponent(String(data.filterType));
    }

    const url =
      environment.apiBaseUrl +
      'v1/repository/repository/searchByCriteria?offset=' +
      (data.page - 1) +
      '&limit=' +
      data.size +
      '&search=' +
      encodeURIComponent(String(data.search ?? '')) +
      '&sortField=' +
      encodeURIComponent(String(data.sortBy ?? '')) +
      '&sortOrder=' +
      encodeURIComponent(String(data.sortOrder ?? '')) +
      urlAppend;

    return this.http.post<{ data?: any[]; total?: number }>(url, data.filter);
  }

  titleCaseWord(word: string): string {
    if (!word) return word;
    return word[0].toUpperCase() + word.substr(1).toLowerCase();
  }
  isProtectedMediaUrl(fileUrl: string): boolean {
    if (!fileUrl) {
      return false;
    }
    return fileUrl.includes('/v1/media/content');
  }

  /** Stable identity for a media URL (blob name). Ignores changing exp/sig. */
  getProtectedMediaCacheKey(fileUrl: string): string {
    if (!fileUrl) {
      return '';
    }
    if (!this.isProtectedMediaUrl(fileUrl)) {
      return fileUrl.trim();
    }
    try {
      const blob = new URL(fileUrl, window.location.origin).searchParams.get('blob');
      if (blob) {
        return `media:${blob}`;
      }
    } catch {
      // fall through
    }
    return fileUrl.trim();
  }

  isSameMediaUrl(a: string | null | undefined, b: string | null | undefined): boolean {
    const left = String(a ?? '').trim();
    const right = String(b ?? '').trim();
    if (!left && !right) {
      return true;
    }
    if (!left || !right) {
      return false;
    }
    if (left === right) {
      return true;
    }
    return this.getProtectedMediaCacheKey(left) === this.getProtectedMediaCacheKey(right);
  }

  getFileExtensionFromUrl(fileUrl: string): string {
    if (!fileUrl) {
      return '';
    }
    try {
      const parsed = new URL(fileUrl, window.location.origin);
      const name = parsed.searchParams.get('name') || parsed.searchParams.get('blob') || '';
      if (name) {
        const fromName = name.split('/').pop()?.split('.').pop()?.toLowerCase() || '';
        if (fromName) {
          return fromName;
        }
      }
      const ct = (parsed.searchParams.get('ct') || '').toLowerCase();
      if (ct.includes('pdf')) return 'pdf';
      if (ct.includes('png')) return 'png';
      if (ct.includes('jpeg') || ct.includes('jpg')) return 'jpg';
      if (ct.includes('gif')) return 'gif';
      if (ct.includes('webp')) return 'webp';
      if (ct.includes('msword') || ct.includes('wordprocessingml')) return 'docx';
    } catch {
      // fall through
    }
    return fileUrl.split('.').pop()?.split('?')[0]?.toLowerCase() || '';
  }

  fetchProtectedMediaObjectUrl(fileUrl: string): Observable<string> {
    const cacheKey = this.getProtectedMediaCacheKey(fileUrl);
    const cached = this.mediaObjectUrlCache.get(cacheKey);
    if (cached) {
      return new Observable((subscriber) => {
        subscriber.next(cached);
        subscriber.complete();
      });
    }
    const inflight = this.mediaObjectUrlInflight.get(cacheKey);
    if (inflight) {
      return inflight;
    }
    const request$ = this.http.get(fileUrl, { responseType: 'blob' }).pipe(
      map((blob) => URL.createObjectURL(blob)),
      tap((objectUrl) => {
        this.mediaObjectUrlCache.set(cacheKey, objectUrl);
        this.mediaObjectUrlInflight.delete(cacheKey);
      }),
      shareReplay(1),
    );
    this.mediaObjectUrlInflight.set(cacheKey, request$);
    return request$;
  }

  /** Drop cached blob URL so a replaced file at the same blob path reloads. */
  invalidateProtectedMediaCache(fileUrl: string | null | undefined): void {
    const cacheKey = this.getProtectedMediaCacheKey(String(fileUrl ?? ''));
    if (!cacheKey) {
      return;
    }
    const existing = this.mediaObjectUrlCache.get(cacheKey);
    if (existing) {
      URL.revokeObjectURL(existing);
      this.mediaObjectUrlCache.delete(cacheKey);
    }
    this.mediaObjectUrlInflight.delete(cacheKey);
  }

  fileViewClick(fileUrl: any) {
    if (!fileUrl) {
      return;
    }
    if (this.isProtectedMediaUrl(fileUrl)) {
      this.fetchProtectedMediaObjectUrl(fileUrl).subscribe({
        next: (objectUrl) => window.open(objectUrl, '_blank'),
        error: () => this.showError('Unable to open document.', 'Media'),
      });
      return;
    }
    const embedUrl = this.getFileViewEmbedUrl(fileUrl);
    if (!embedUrl) {
      return;
    }
    window.open(embedUrl, '_blank');
  }

  getFileViewEmbedUrl(fileUrl: string): string {
    if (!fileUrl) {
      return '';
    }
    if (this.isProtectedMediaUrl(fileUrl)) {
      return fileUrl;
    }

    const extension = this.getFileExtensionFromUrl(fileUrl);
    if (extension === 'pdf') {
      return fileUrl;
    }
    if (extension === 'doc' || extension === 'docx') {
      return `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
    }
    return fileUrl;
  }

  isImageFileUrl(fileUrl: string): boolean {
    const extension = this.getFileExtensionFromUrl(fileUrl);
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(extension);
  }

  /**
   * Normalizes barcode/QR API responses into a browser-safe image source.
   * Supports direct URLs, data URLs, raw base64, and nested JSON payloads.
   */
  resolveQrImageFromApiResponse(res: unknown): { imageSrc: string; rawBase64Payload: string } {
    const parsed = this.coerceBarcodeResponse(res);
    const resolved = this.resolveBase64ToDataUrl(parsed);
    if (!resolved) {
      return { imageSrc: '', rawBase64Payload: '' };
    }
    return resolved;
  }

  private coerceBarcodeResponse(res: unknown): unknown {
    if (res == null) {
      return null;
    }
    if (typeof res === 'object') {
      return res;
    }
    if (typeof res === 'string') {
      return this.parseBarcodeJsonString(res);
    }
    return null;
  }

  private parseBarcodeJsonString(body: string): unknown {
    const trimmed = body.replace(/^\uFEFF/, '').trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.parse(trimmed) as unknown;
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  private resolveBase64ToDataUrl(res: unknown): { imageSrc: string; rawBase64Payload: string } | null {
    if (res == null) {
      return null;
    }

    if (typeof res === 'object' && res !== null && 'barcodeBase64' in res) {
      const b64 = (res as Record<string, unknown>)['barcodeBase64'];
      if (typeof b64 === 'string' && b64.trim()) {
        return this.normalizeBase64String(this.stripBase64Noise(b64));
      }
    }

    if (typeof res === 'string') {
      const asUrl = this.asHttpImageUrl(res);
      if (asUrl) {
        return { imageSrc: asUrl, rawBase64Payload: '' };
      }
      return this.normalizeBase64String(this.stripBase64Noise(res));
    }

    if (typeof res === 'object') {
      const o = res as Record<string, unknown>;
      const candidates = [
        o['barcodeBase64'],
        o['base64'],
        o['base64Content'],
        o['content'],
        o['imageBase64'],
        o['qrCode'],
        o['qrImage'],
        o['image'],
        o['barcode'],
        o['fileContent'],
        o['payload'],
        o['result'],
        o['data'],
        o['url'],
        o['signedUrl'],
        o['publicUrl'],
        (o['data'] as Record<string, unknown>)?.['barcodeBase64'],
        (o['data'] as Record<string, unknown>)?.['base64'],
        (o['data'] as Record<string, unknown>)?.['content'],
        (o['data'] as Record<string, unknown>)?.['imageBase64'],
        (o['result'] as Record<string, unknown>)?.['content'],
        (o['result'] as Record<string, unknown>)?.['base64'],
      ];

      for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) {
          const asUrl = this.asHttpImageUrl(c);
          if (asUrl) {
            return { imageSrc: asUrl, rawBase64Payload: '' };
          }
          const resolved = this.normalizeBase64String(this.stripBase64Noise(c));
          if (resolved) {
            return resolved;
          }
        }
      }

      const fromDeep = this.findFirstBase64LikeString(o, 0, 5);
      if (fromDeep) {
        return this.normalizeBase64String(fromDeep);
      }
    }

    return null;
  }

  private findFirstBase64LikeString(
    obj: Record<string, unknown>,
    depth: number,
    maxDepth: number
  ): string | null {
    if (depth > maxDepth || !obj) {
      return null;
    }
    for (const value of Object.values(obj)) {
      if (typeof value === 'string') {
        const s = this.stripBase64Noise(value);
        if (this.looksLikeBase64Payload(s)) {
          return s;
        }
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        const nested = this.findFirstBase64LikeString(value as Record<string, unknown>, depth + 1, maxDepth);
        if (nested) {
          return nested;
        }
      } else if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'string') {
            const s = this.stripBase64Noise(item);
            if (this.looksLikeBase64Payload(s)) {
              return s;
            }
          } else if (item && typeof item === 'object') {
            const nested = this.findFirstBase64LikeString(item as Record<string, unknown>, depth + 1, maxDepth);
            if (nested) {
              return nested;
            }
          }
        }
      }
    }
    return null;
  }

  private looksLikeBase64Payload(s: string): boolean {
    if (s.length < 32) {
      return false;
    }
    if (s.startsWith('data:')) {
      return true;
    }
    const sample = s.slice(0, 256).replace(/\s/g, '');
    return /^[A-Za-z0-9+/]+=*$/.test(sample);
  }

  private stripBase64Noise(value: string): string {
    return value.replace(/\s/g, '').replace(/\r|\n/g, '');
  }

  private asHttpImageUrl(value: string): string | null {
    const t = value.trim();
    if (t.startsWith('http://') || t.startsWith('https://')) {
      return t;
    }
    return null;
  }

  private normalizeBase64String(value: string): { imageSrc: string; rawBase64Payload: string } | null {
    let trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith('data:')) {
      return { imageSrc: trimmed, rawBase64Payload: '' };
    }

    trimmed = this.stripBase64Noise(trimmed);
    if (!trimmed) {
      return null;
    }

    const mime = this.guessImageMimeFromBase64(trimmed);
    return {
      imageSrc: `data:${mime};base64,${trimmed}`,
      rawBase64Payload: trimmed,
    };
  }

  private guessImageMimeFromBase64(b64: string): string {
    if (b64.startsWith('/9j/')) {
      return 'image/jpeg';
    }
    if (b64.startsWith('iVBORw0KGgo')) {
      return 'image/png';
    }
    if (b64.startsWith('R0lGOD')) {
      return 'image/gif';
    }
    if (b64.startsWith('PHN2Zy') || b64.startsWith('PD94bWwg')) {
      return 'image/svg+xml';
    }
    return 'image/png';
  }
}
